/**
 * AutoSafetyShareToggle
 * ---------------------------------------------------------------
 * Member-facing setting that controls whether new events and dates
 * created through the app automatically share location/details with
 * the member's emergency circle.
 *
 * Default: ON. Members can opt out at any time.
 * Persisted on `profiles.auto_safety_share_default`.
 */
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useProfile } from "@/lib/profile/useProfile";
import { toast } from "sonner";

const AutoSafetyShareToggle = () => {
  const { profile, update } = useProfile();
  const [busy, setBusy] = useState(false);
  // profile may not yet expose this typed; default to true (matches DB default).
  const value = (profile as unknown as { auto_safety_share_default?: boolean } | null)
    ?.auto_safety_share_default ?? true;

  const onChange = async (next: boolean) => {
    setBusy(true);
    const { error } = await update({
      auto_safety_share_default: next,
    } as Record<string, unknown>);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(next ? "Auto safety share is on." : "Auto safety share is off.");
  };

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4 bg-card">
      <div className="space-y-1">
        <Label htmlFor="auto-safety-share" className="text-sm font-medium">
          Auto-share new events & dates with your emergency circle
        </Label>
        <p className="text-xs text-muted-foreground">
          When on, every event or date you create through the app starts with
          safety-share enabled by default. You can still turn it off per
          event. We strongly recommend keeping this on.
        </p>
      </div>
      <Switch
        id="auto-safety-share"
        checked={value}
        disabled={busy}
        onCheckedChange={onChange}
      />
    </div>
  );
};

export default AutoSafetyShareToggle;
